"use client";
import {ServerRouter, type ServerRouterInstance} from "@/infrastructure/navigation";
import {proxy} from "valtio";

import type {DestinationCardData} from "@/entities/destination/model";

import type {IValtioModel} from "@/shared/models/base-model.interface";
import {Field, type FieldInstance} from "@/shared/models/form/fields";

export interface DestinationSelectorConfig {
    destinations?: Array<DestinationCardData>;
    onSelect?: (destination: DestinationCardData) => void;
}

class DestinationSelectorClass implements IValtioModel {
    destinations: Array<DestinationCardData>;
    isOpen: boolean;
    searchInput: FieldInstance;
    filteredDestinations: Array<DestinationCardData>;
    selectedDestination: DestinationCardData | null;
    highlightedIndex: number;
    onSelect?: (destination: DestinationCardData) => void;
    serverRouter: ServerRouterInstance;
    isNavigating: boolean;

    constructor(config: DestinationSelectorConfig = {}) {
        this.destinations = config.destinations || [];
        this.onSelect = config.onSelect;
        this.isOpen = false;

        this.searchInput = Field({initialValue: ""});
        this.filteredDestinations = [];
        this.selectedDestination = null;
        this.highlightedIndex = -1;
        this.serverRouter = ServerRouter();
        this.isNavigating = false;
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public init(): void {}
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public dispose(): void {}

    /**
     * Update filtered destinations based on search input
     */
    updateFilteredDestinations(): void {
        const query = this.searchInput.value.toLowerCase().trim();
        if (query === "") {
            this.filteredDestinations = this.destinations;
        } else {
            this.filteredDestinations = this.destinations.filter((dest) => dest.title.toLowerCase().includes(query));
        }
    }

    /**
     * Handle search input change
     */
    onSearchChange(value: string): void {
        this.searchInput.setValue(value);
        this.updateFilteredDestinations();
        this.isOpen = this.filteredDestinations.length > 0;
    }

    /**
     * Handle input focus
     */
    onFocus(): void {
        this.updateFilteredDestinations();
        this.isOpen = this.filteredDestinations.length > 0;
        this.highlightedIndex = -1; // Reset highlight on focus
    }

    /**
     * Move highlight down
     */
    moveHighlightDown(): void {
        if (!this.isOpen && this.filteredDestinations.length > 0) {
            this.open();
            this.highlightedIndex = 0;
        } else if (this.isOpen && this.filteredDestinations.length > 0) {
            this.highlightedIndex = this.highlightedIndex < this.filteredDestinations.length - 1 ? this.highlightedIndex + 1 : 0;
        }
    }

    /**
     * Move highlight up
     */
    moveHighlightUp(): void {
        if (this.isOpen && this.filteredDestinations.length > 0) {
            this.highlightedIndex = this.highlightedIndex > 0 ? this.highlightedIndex - 1 : this.filteredDestinations.length - 1;
        }
    }

    /**
     * Select highlighted destination (first step)
     */
    selectHighlighted(): void {
        if (this.isOpen && this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredDestinations.length) {
            this.selectedDestination = this.filteredDestinations[this.highlightedIndex];
            this.searchInput.setValue(this.selectedDestination.title);
            this.isOpen = false;
            this.highlightedIndex = -1;
        }
    }

    /**
     * Select a destination
     */
    selectDestination(destination: DestinationCardData): void {
        this.searchInput.setValue(destination.title);
        this.selectedDestination = destination;
        this.isOpen = false;
        this.highlightedIndex = -1;
        this.isNavigating = true;
        // Navigate to destination page
        this.serverRouter.navigate(destination.url);

        this.onSelect?.(destination);
    }

    /**
     * Handle enter key press or button click
     */
    onEnter(): void {
        if (this.isOpen && this.highlightedIndex >= 0) {
            this.selectHighlighted();
            return;
        }
        if (this.selectedDestination) {
            this.selectDestination(this.selectedDestination);
            return;
        }

        if (this.isOpen && this.filteredDestinations.length > 0) {
            this.selectedDestination = this.filteredDestinations[0];
            this.searchInput.setValue(this.selectedDestination.title);
            this.isOpen = false;
            this.highlightedIndex = -1;
            return;
        }

        if (this.searchInput.value.trim()) {
            const query = this.searchInput.value.toLowerCase().trim();
            const match = this.destinations.find((dest) => dest.title.toLowerCase() === query);
            if (match) {
                this.selectDestination(match);
                return;
            }
            // Navigate to destinations page with search
            this.isNavigating = true;
            this.serverRouter.navigate("/destinations/");
            return;
        }

        // If no search text, navigate to destinations page
        this.isNavigating = true;
        this.serverRouter.navigate("/destinations/");
    }

    /**
     * Toggle dropdown open/close
     */
    toggle(): void {
        this.isOpen = !this.isOpen;
    }

    /**
     * Close dropdown
     */
    close(): void {
        this.isOpen = false;
        this.highlightedIndex = -1;
        this.selectedDestination = null;
    }

    /**
     * Open dropdown
     */
    open(): void {
        this.isOpen = true;
        this.updateFilteredDestinations();
    }
}

export type DestinationSelectorInstance = InstanceType<typeof DestinationSelectorClass>;

export function DestinationSelector(config: DestinationSelectorConfig = {}): DestinationSelectorInstance {
    return proxy(new DestinationSelectorClass(config));
}
